import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "wasp/client/auth";

// Routes that should never be intercepted by the onboarding guard
const PUBLIC_PATHS = new Set([
  "/login",
  "/signup",
  "/logout",
  "/onboarding", // let users access onboarding
  "/verify-email",
  "/password-reset",
]);

// allow public shared links /r/:slug, /p/:slug etc.
const isPublicPath = (pathname: string) =>
  PUBLIC_PATHS.has(pathname) ||
  pathname.startsWith("/r/") ||
  pathname.startsWith("/p/") ||
  pathname.startsWith("/embed/");

function DomainRouterInner({ children }: React.PropsWithChildren) {
  const { data: user, isLoading } = useAuth();
  const { pathname } = useLocation();

  // While auth is resolving, render children to keep skeletons/layout
  if (isLoading) return <>{children}</>;

  // If not logged in, let the wasp auth gate handle redirecting to /login.
  // We don't interfere here to avoid fighting createAuthRequiredPage.
  if (!user) return <>{children}</>;

  // Check for escape hatch (used after onboarding completion to allow dashboard access)
  const justCompleted = localStorage.getItem('onboardingJustCompleted') === '1';
  if (justCompleted) {
    // Clear the flag and let them through regardless of onboarding status
    localStorage.removeItem('onboardingJustCompleted');
    return <>{children}</>;
  }

  // Hardwall to onboarding until completed.
  // Adjust the flag name to match your schema (e.g., user.onboardingComplete).
  const needsOnboarding = user && (user as any).onboardingComplete === false;

  // Allow dashboard access if user explicitly navigated there post-completion
  if (pathname.startsWith('/dashboard')) {
    return <>{children}</>;
  }

  if (needsOnboarding && !isPublicPath(pathname)) {
    // Avoid loops: if the user is already on /onboarding, don't redirect
    if (pathname !== "/onboarding") {
      return <Navigate to="/onboarding" replace state={{ from: pathname }} />;
    }
  }

  return <>{children}</>;
}

export const DomainRouter: React.FC<React.PropsWithChildren> = ({ children }) => {
  // Dev-safe guards: optionally disable via env and wait until QueryClient exists
  const disable = (import.meta as any)?.env?.VITE_DISABLE_DOMAIN_ROUTER === '1';
  const hasQueryClient = typeof window !== 'undefined' && !!(window as any).__NURA_QC__;
  if (disable || !hasQueryClient) return <>{children}</>;
  return <DomainRouterInner>{children}</DomainRouterInner>;
}

export default DomainRouter;
